import { INTERCOM_APP_ID, INTERCOM_DOMAIN } from "../config/config";

/**
 * Intercom Integration Helper
 * Provides comprehensive Intercom functionality including initialization,
 * configuration management, script loading, and user management
 *

 */

// Get host info to check if this is AiSensy

// Global Intercom state
let intercomInitialized = false;
let intercomScriptLoaded = false;
let pendingCommands = [];

/**
 * Initialize Intercom with domain and app ID from .env
 * @param {Object} userData - User data for initialization (optional)
 * @returns {Promise<boolean>} Promise that resolves to true if successful
 */
export const initializeIntercom = async (userData = {}) => {
  try {
    // If userData is provided, use initializeIntercomWithUser
    if (userData && Object.keys(userData).length > 0) {
      await initializeIntercomWithUser(userData, {});
      return true;
    }

    // Otherwise use basic initialization with widget hidden
    const config = constructIntercomConfig({ hide_default_launcher: true });

    // Check if script is loaded, if not load it
    if (!isIntercomScriptLoaded()) {
      await loadIntercomScript();
    }

    // Boot Intercom with configuration
    bootIntercom(config);

    console.log("Intercom initialized successfully", config);
    return true;
  } catch (error) {
    console.error("Error initializing Intercom:", error);
    return false;
  }
};

/**
 * Construct the Intercom configuration object
 * @param {Object} customSettings - Additional custom settings
 * @returns {Object} Intercom configuration
 */
export const constructIntercomConfig = (customSettings = {}) => {
  const baseConfig = {
    app_id: INTERCOM_APP_ID,
    widget: {
      activator: "#intercom-launcher",
    },
    // Default settings
    hide_default_launcher: false,
    alignment: "right",
    vertical_padding: 20,
    horizontal_padding: 20,
    ...customSettings,
  };

  // Add environment-specific settings
  if (process.env.NODE_ENV !== "production") {
    baseConfig.debug = true;
  }

  return baseConfig;
};

/**
 * Check if the Intercom script is loaded in the document
 * @returns {boolean} True if script is loaded
 */
export const isIntercomScriptLoaded = () => {
  if (typeof window === "undefined") return false;

  // Check if script tag exists
  const scriptExists = document.querySelector(
    `script[src*="${INTERCOM_DOMAIN}"]`
  );

  // Check if Intercom object exists on window
  const intercomExists =
    window.Intercom && typeof window.Intercom === "function";

  intercomScriptLoaded = !!(scriptExists && intercomExists);
  return intercomScriptLoaded;
};

/**
 * Check if Intercom is already initialized
 * @returns {boolean} True if Intercom is initialized
 */
export const isIntercomInitialized = () => {
  if (typeof window === "undefined") return false;

  try {
    // Check if Intercom is available and has been booted
    const isAvailable =
      window.Intercom && typeof window.Intercom === "function";
    const isBooted = window.intercomSettings && window.intercomSettings.app_id;

    intercomInitialized = !!(isAvailable && isBooted);
    return intercomInitialized;
  } catch (error) {
    console.error("Error checking Intercom initialization:", error);
    return false;
  }
};

/**
 * Helper function to dynamically load the Intercom script
 * @returns {Promise} Promise that resolves when script is loaded
 */
export const loadIntercomScript = () => {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("Window object not available"));
      return;
    }

    // Check if script is already loaded
    if (isIntercomScriptLoaded()) {
      resolve();
      return;
    }

    // Check if script is already in the document
    const existingScript = document.querySelector(
      `script[src*="${INTERCOM_DOMAIN}"]`
    );
    if (existingScript) {
      existingScript.onload = () => {
        intercomScriptLoaded = true;
        resolve();
      };
      existingScript.onerror = reject;
      return;
    }

    // Create and load the script
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.async = true;
    script.src = `https://${INTERCOM_DOMAIN}/widget/${INTERCOM_APP_ID}`;

    script.onload = () => {
      intercomScriptLoaded = true;
      console.log("Intercom script loaded successfully");
      resolve();
    };

    script.onerror = (error) => {
      console.error("Failed to load Intercom script:", error);
      reject(error);
    };

    // Add to document head
    const firstScript = document.getElementsByTagName("script")[0];
    firstScript.parentNode.insertBefore(script, firstScript);
  });
};

/**
 * Dynamically load/update Intercom with the Intercom ID
 * @param {string} appId - Custom app ID (optional)
 * @returns {Promise} Promise that resolves when Intercom is ready
 */
export const loadOrUpdateIntercom = async (appId = null) => {
  try {
    const actualAppId = appId || INTERCOM_APP_ID;

    // Load (or re-check) the Intercom script
    await loadIntercomScript();

    // Construct configuration with the app ID
    const config = constructIntercomConfig({ app_id: actualAppId });

    // Boot or update Intercom
    if (isIntercomInitialized()) {
      updateIntercomSettings(config);
    } else {
      bootIntercom(config);
    }

    return config;
  } catch (error) {
    console.error("Error loading/updating Intercom:", error);
    throw error;
  }
};

/**
 * Set or merge Intercom settings
 * @param {Object} newSettings - New settings to merge
 * @param {boolean} merge - Whether to merge with existing settings
 */
export const setIntercomSettings = (newSettings, merge = true) => {
  try {
    if (typeof window === "undefined") return;

    const currentSettings = window.intercomSettings || {};

    if (merge) {
      window.intercomSettings = {
        ...currentSettings,
        ...newSettings,
      };
    } else {
      window.intercomSettings = newSettings;
    }

    console.log("Intercom settings updated:", window.intercomSettings);
  } catch (error) {
    console.error("Error setting Intercom settings:", error);
  }
};

/**
 * Boot Intercom with the assigned settings
 * @param {Object} settings - Intercom settings
 */
export const bootIntercom = (settings) => {
  try {
    if (typeof window === "undefined") {
      console.warn("Window object not available, cannot boot Intercom");
      return;
    }

    if (!isIntercomScriptLoaded()) {
      console.warn("Intercom script not loaded, queueing boot command");
      queueIntercomCommand("boot", settings);
      return;
    }

    // Check if Intercom is already running and update instead of boot
    if (isIntercomInitialized()) {
      console.log("Intercom already initialized, updating instead of booting");
      window.Intercom("update", settings);
    } else {
      // Set global settings
      setIntercomSettings(settings, false);

      // Boot Intercom
      window.Intercom("boot", settings);
      intercomInitialized = true;
    }

    // Execute any pending commands
    executePendingCommands();

    console.log("Intercom boot/update completed successfully");
  } catch (error) {
    console.error("Error booting Intercom:", error);
  }
};

/**
 * Update Intercom settings for already initialized instance
 * @param {Object} settings - New settings to update
 */
export const updateIntercomSettings = (settings) => {
  try {
    if (!isIntercomInitialized()) {
      console.warn("Intercom not initialized, cannot update settings");
      return;
    }

    // Merge settings
    setIntercomSettings(settings, true);

    // Update Intercom
    window.Intercom("update", settings);

    console.log("Intercom settings updated successfully");
  } catch (error) {
    console.error("Error updating Intercom settings:", error);
  }
};

/**
 * Queue up Intercom commands when not initialized
 * @param {string} method - Intercom method to call
 * @param {*} data - Data to pass to method
 */
export const queueIntercomCommand = (method, data = null) => {
  pendingCommands.push({ method, data, timestamp: Date.now() });
  console.log(`Queued Intercom command: ${method}`, data);
};

/**
 * Execute all pending Intercom commands
 */
export const executePendingCommands = () => {
  if (!isIntercomInitialized() || pendingCommands.length === 0) {
    return;
  }

  try {
    console.log(
      `Executing ${pendingCommands.length} pending Intercom commands`
    );

    pendingCommands.forEach(({ method, data }) => {
      if (data) {
        window.Intercom(method, data);
      } else {
        window.Intercom(method);
      }
    });

    // Clear the queue
    pendingCommands = [];
    console.log("All pending Intercom commands executed");
  } catch (error) {
    console.error("Error executing pending Intercom commands:", error);
  }
};

/**
 * Prepare Intercom payload with user and company data
 * @param {Object} userData - User data
 * @param {Object} companyData - Company data (optional)
 * @returns {Object} Prepared Intercom payload
 */
export const prepareIntercomPayload = (userData = {}, companyData = {}) => {
  console.log("Koca: prepareIntercomPayload userData ", userData);
  try {
    // Ensure we have the required app_id
    const payload = {
      app_id: INTERCOM_APP_ID,
    };

    // Add user identification - user_id takes precedence over email
    if (userData.user_id) {
      payload.user_id = String(userData.user_id);
    }

    if (userData.email) {
      payload.email = userData.email;
    }

    // Add basic user information
    if (userData.name) {
      payload.name = userData.name;
    }

    if (userData.phone) {
      payload.phone = userData.phone;
    }

    if (userData.created_at) {
      payload.created_at = userData.created_at;
    }

    // Validate that we have at least one identifier
    if (!payload.user_id && !payload.email) {
      console.error(
        "❌ [Intercom] Missing required user identification (user_id or email)"
      );
      return null;
    }

    console.log("🚀 Intercom payload prepared:", payload);
    return payload;
  } catch (error) {
    console.error("Error preparing Intercom payload:", error);
    return null;
  }
};

/**
 * Initialize Intercom with user data
 * @param {Object} userData - User data for Intercom
 * @param {Object} companyData - Company data for Intercom (optional)
 * @returns {Promise} Promise that resolves when Intercom is fully initialized
 */
export const initializeIntercomWithUser = async (userData = {}) => {
  try {
    // Validate user data
    if (!userData || Object.keys(userData).length === 0) {
      throw new Error("No user data provided");
    }

    // Prepare the payload
    const payload = prepareIntercomPayload(userData);

    if (!payload) {
      throw new Error(
        "Failed to prepare Intercom payload - missing user identification"
      );
    }

    // Load Intercom script if not already loaded
    if (!isIntercomScriptLoaded()) {
      console.log("📥 Loading Intercom script...");
      await loadIntercomScript();
    }

    // Ensure user settings are stored in window.intercomSettings for future use
    setIntercomSettings(payload, false);

    // Boot Intercom with user data
    bootIntercom(payload);

    // Verify initialization
    if (isIntercomInitialized()) {
      console.log("✅ Intercom successfully initialized with user data");

      // Additional verification - check if user is properly identified
      setTimeout(() => {
        try {
          if (window.Intercom && typeof window.Intercom === "function") {
            console.log("🔍 Verifying Intercom user identification...");
            // You can add additional verification here if needed
          }
        } catch (error) {
          console.warn(
            "⚠️ Could not verify Intercom user identification:",
            error
          );
        }
      }, 1000);

      return payload;
    } else {
      throw new Error(
        "Intercom initialization failed - not properly initialized"
      );
    }
  } catch (error) {
    console.error("❌ Error initializing Intercom with user:", error);
    throw error;
  }
};

/**
 * Utility functions for common Intercom operations
 */
export const IntercomUtils = {
  // Show the Intercom messenger
  show: () => {
    if (isIntercomInitialized()) {
      window.Intercom("show");
    } else {
      queueIntercomCommand("show");
    }
  },

  // Hide the Intercom messenger
  hide: () => {
    if (isIntercomInitialized()) {
      window.Intercom("hide");
    } else {
      queueIntercomCommand("hide");
    }
  },

  // Update user data
  updateUser: (userData) => {
    const payload = prepareIntercomPayload(userData);
    if (isIntercomInitialized()) {
      window.Intercom("update", payload);
    } else {
      queueIntercomCommand("update", payload);
    }
  },

  // Track an event
  trackEvent: (eventName, metadata = {}) => {
    if (isIntercomInitialized()) {
      window.Intercom("trackEvent", eventName, metadata);
    } else {
      queueIntercomCommand("trackEvent", { eventName, metadata });
    }
  },

  // Start a new conversation
  startConversation: () => {
    if (isIntercomInitialized()) {
      window.Intercom("showNewMessage");
    } else {
      queueIntercomCommand("showNewMessage");
    }
  },

  // Get visitor ID
  getVisitorId: () => {
    if (isIntercomInitialized() && window.Intercom) {
      return window.Intercom("getVisitorId");
    }
    return null;
  },

  // Shutdown Intercom
  shutdown: () => {
    if (isIntercomInitialized()) {
      window.Intercom("shutdown");
      intercomInitialized = false;
    }
  },

  // Check if messenger is open
  isOpen: () => {
    if (isIntercomInitialized()) {
      return window.Intercom("isOpen");
    }
    return false;
  },
};

// Dashboard-compatible utility functions
/**
 * Check if Intercom is available and ready to use
 * @returns {boolean} True if Intercom is available
 */
export const isIntercomAvailable = () => {
  return isIntercomInitialized();
};

/**
 * Track an Intercom event (dashboard-compatible function)
 * @param {string} eventName - Event name
 * @param {Object} metadata - Event metadata
 */
export const trackIntercomEvent = (eventName, metadata = {}) => {
  try {
    if (isIntercomInitialized()) {
      window.Intercom("trackEvent", eventName, metadata);
      console.log(`[Intercom] Event tracked: ${eventName}`, metadata);
      return true;
    } else {
      console.warn("[Intercom] Not initialized, queueing event");
      queueIntercomCommand("trackEvent", { eventName, metadata });
      return false;
    }
  } catch (error) {
    console.error("[Intercom] Error tracking event:", error);
    return false;
  }
};

/**
 * Update Intercom user attributes (dashboard-compatible function)
 * @param {Object} attributes - User attributes to update
 */
export const updateIntercomUserAttributes = (attributes = {}) => {
  try {
    console.log("🔄 Updating Intercom user attributes:", attributes);

    if (!isIntercomInitialized()) {
      console.warn("[Intercom] Not initialized, cannot update attributes");
      return false;
    }

    // Get current Intercom settings to extract user identification
    const currentSettings = window.intercomSettings || {};

    // Prepare the update payload with user identification (using working documentation approach)
    const updatePayload = {
      app_id: INTERCOM_APP_ID,
      ...attributes,
    };

    // Include user identification from current settings
    if (currentSettings.email) {
      updatePayload.email = currentSettings.email;
    }

    if (currentSettings.user_id) {
      updatePayload.user_id = currentSettings.user_id;
    }

    // If no user identification found, try to get it from the current session
    if (!updatePayload.email && !updatePayload.user_id) {
      console.warn(
        "[Intercom] No user identification found, attributes may not be saved properly"
      );

      // Try to get visitor ID as fallback
      try {
        const visitorId = window.Intercom("getVisitorId");
        if (visitorId) {
          updatePayload.user_id = visitorId;
        }
      } catch (err) {
        console.warn("[Intercom] Could not get visitor ID:", err);
      }
    }

    console.log("📦 [Intercom] Update payload:", updatePayload);

    // Use the working documentation approach:
    // 1. Update window.intercomSettings directly
    window.intercomSettings = updatePayload;

    // 2. Also call update method
    window.Intercom("update", updatePayload);

    // 3. Use the proven shutdown/boot approach for reliable updates
    setTimeout(() => {
      console.log(
        "🔄 [Intercom] Using documentation approach (shutdown/boot) for reliable update..."
      );
      window.Intercom("shutdown");
      setTimeout(() => {
        window.Intercom("boot", updatePayload);
        console.log("✅ [Intercom] Documentation approach completed");
      }, 500);
    }, 1000);

    console.log(
      "✅ [Intercom] User attributes update initiated (using documentation approach)"
    );
    return true;
  } catch (error) {
    console.error("❌ [Intercom] Failed to update user attributes:", error);
    return false;
  }
};

/**
 * Ensure user is properly identified in Intercom with fallback data
 * @param {Object} fallbackUserData - Fallback user data if no identification found
 * @returns {boolean} True if user is identified
 */
export const ensureUserIdentified = (fallbackUserData = {}) => {
  try {
    const currentSettings = window.intercomSettings || {};

    // Check if user is already identified
    if (currentSettings.email || currentSettings.user_id) {
      return true;
    }

    // If not identified, try to identify with fallback data
    if (fallbackUserData.email || fallbackUserData.user_id) {
      const identificationPayload = {
        app_id: INTERCOM_APP_ID,
        ...fallbackUserData,
      };

      // Update Intercom with user identification
      window.Intercom("update", identificationPayload);

      // Also update the settings
      setIntercomSettings(identificationPayload, true);

      return true;
    }

    console.warn(
      "⚠️ [Intercom] Cannot identify user - no email or user_id provided"
    );
    return false;
  } catch (error) {
    console.error("❌ [Intercom] Error ensuring user identification:", error);
    return false;
  }
};

/**
 * Send comprehensive user attributes to Intercom after successful login
 * This should be called only once after login success
 */
export const sendIntercomAttributesAfterLogin = async (
  agent,
  assistantDetails,
  agents
) => {
  try {
    if (!agent) {
      console.log("sendIntercomAttributesAfterLogin: Missing agent data");
      return false;
    }

    // Match agent ID with agents response to get UTM parameters and app download status
    let matchedAgentData = null;
    if (
      agents &&
      typeof agents === "object" &&
      Object.keys(agents).length > 0
    ) {
      // Find matching agent by _id using find() method
      matchedAgentData = Object.values(agents).find(
        (agentData) => agent.id === agentData._id
      );

      if (matchedAgentData) {
        console.log("✅ Found matching agent data:", matchedAgentData);
      } else {
        console.warn("⚠️ No matching agent data found for agent ID:", agent.id);
      }
    }

    // Ensure Intercom is available
    if (!isIntercomAvailable()) {
      const userData = {
        email: agent?.email || "unknown@example.com",
        name: agent?.displayName || assistantDetails?.name || "Unknown User",
        user_id:
          `${agent.id}_${assistantDetails.assistantId}` || "unknown_user",
        created_at: Math.floor(Date.now() / 1000),
      };

      await initializeIntercom(userData);
    }

    // Ensure user is identified
    ensureUserIdentified({
      email: agent?.email,
      name: agent?.displayName || assistantDetails?.name || "Unknown User",
      name: agent?.displayName || agent?.name,
    });

    const isMobileDevice = (deviceString) => {
      if (!deviceString) return false;
      const lower = deviceString?.toLowerCase();
      return (
        lower.includes("iphone") ||
        lower.includes("ipad") ||
        lower.includes("android") ||
        lower.includes("mobile") ||
        lower.includes("phone")
      );
    };

    // Use matched agent data for app download status if available, otherwise fallback to original agent data
    const sessionsToCheck =
      matchedAgentData?.sessions[0]?.device || "Not provided";
    const userAppDownloaded = Array?.isArray(sessionsToCheck)
      ? sessionsToCheck?.some((session) => isMobileDevice(session?.device))
      : false;

    // Prepare comprehensive attributes
    const intercomAttributes = {
      // User signup info
      user_type:
        agent?.power === 2 ? "OWNER" : agent?.power === 1 ? "MANAGER" : "AGENT",
      user_name: agent?.displayName || agent?.name || "Unknown User",
      user_email: agent?.email || "unknown@example.com",
      user_phone_number: agent?.contact || "Not provided",
      user_country: agent?.country || "Unknown",
      user_currency: agent?.currency || "INR",
      project_id: agent?.firstProjectId || "N/A",

      user_referred_by_affiliate: agent?.affiliateId ? true : false,
      user_signup_source: matchedAgentData?.signupSource || "Not specified",

      // UTM parameters - use matched agent data if available, otherwise fallback to original agent data
      user_utm_campaign:
        matchedAgentData?.utmParams?.campaign || "Not specified",
      user_utm_medium: matchedAgentData?.utmParams?.medium || "Not specified",
      user_utm_source: matchedAgentData?.utmParams?.source || "Not specified",

      // Device and app info
      user_device_type: sessionsToCheck || "Not specified",
      user_industry: agent?.industry || "Not specified",
      user_app_download_status: userAppDownloaded,

      // Company info
      owner_phone_number: agent?.contact || "",
      owner_Company_name: agent?.company || "",
      owner_Company_size: agent?.companySize || "",
      Project_name: assistantDetails?.assistantName || "",
      project_mm_lite_onboarded:
        assistantDetails?.mmLiteConfig?.enabled === true,
      project_embedded_signup_url:
        assistantDetails?.embeddedSignupUrl || "URL un-available",
      // Do not remove test attribute
      // user_affiliate_type: "Test attribute 123456",
    };

    const attributeCount = Object.keys(intercomAttributes).length;

    if (attributeCount === 0) {
      throw new Error("No attributes to update");
    }

    // Update attributes
    const updateSuccess = updateIntercomUserAttributes(intercomAttributes);

    if (updateSuccess) {
      console.log(
        `✅ Successfully updated ${attributeCount} Intercom attributes after login`,
        intercomAttributes
      );
      return true;
    } else {
      throw new Error("Failed to update attributes");
    }
  } catch (error) {
    console.error("❌ Failed to send Intercom attributes after login:", error);
    return false;
  }
};

// Auto-initialize Intercom when script is ready
if (typeof window !== "undefined") {
  // Listen for when Intercom script is loaded
  window.addEventListener("load", () => {
    if (isIntercomScriptLoaded() && !isIntercomInitialized()) {
      // Auto-initialize with basic config if not already done
      const basicConfig = constructIntercomConfig();
      bootIntercom(basicConfig);
    }
  });
}

export default {
  initializeIntercom,
  constructIntercomConfig,
  isIntercomScriptLoaded,
  isIntercomInitialized,
  loadIntercomScript,
  loadOrUpdateIntercom,
  setIntercomSettings,
  bootIntercom,
  updateIntercomSettings,
  queueIntercomCommand,
  executePendingCommands,
  prepareIntercomPayload,
  initializeIntercomWithUser,
  IntercomUtils,
  // Dashboard compatibility functions
  isIntercomAvailable,
  trackIntercomEvent,
  updateIntercomUserAttributes,
  ensureUserIdentified,
  sendIntercomAttributesAfterLogin,
};
